import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { CalendarDays, MapPin, Moon, DollarSign, Users, Star, CheckCircle2, X, CreditCard, Award, User, Hourglass, Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import RatingDialog from "@/components/RatingDialog";
import CheckoutReportDialog from "@/components/CheckoutReportDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS_MAP = {
  pending_approval: { label: "En attente d'acceptation", className: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "Acceptée · paiement requis", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Refusée", className: "bg-red-50 text-red-700 border-red-200" },
  expired: { label: "Expirée", className: "bg-zinc-100 text-zinc-500 border-zinc-200" },
  pending: { label: "Paiement en cours", className: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  confirmed: { label: "Confirmée", className: "bg-green-50 text-green-700 border-green-200" },
  cancelled: { label: "Annulée", className: "bg-red-50 text-red-700 border-red-200" },
  completed: { label: "Terminée", className: "bg-blue-50 text-blue-700 border-blue-200" },
};
const PAYMENT_MAP = {
  not_required_yet: { label: "Pas encore requis", className: "bg-zinc-100 text-zinc-500" },
  pending: { label: "En attente", className: "bg-yellow-100 text-yellow-700" },
  paid: { label: "Payé", className: "bg-green-100 text-green-700" },
  failed: { label: "Echoue", className: "bg-red-100 text-red-700" },
};

function formatDate(d) { try { return format(new Date(d), "d MMM yyyy", { locale: fr }); } catch { return d; } }

function getPhotoUrl(photo) {
  if (!photo) return null;
  if (photo.startsWith("/api/")) return `${BACKEND_URL}${photo}`;
  return photo;
}

function UserProfileMini({ name, photo, avgRating, ratingCount, isSuperstar, bio, userId }) {
  const initial = name?.charAt(0)?.toUpperCase() || "?";
  const photoUrl = getPhotoUrl(photo);
  return (
    <Link to={`/users/${userId}`} className="block group" data-testid={`booking-user-link-${userId}`}>
      <div className="flex items-start gap-3 p-3 rounded-xl bg-zinc-50 border border-zinc-100 group-hover:border-emerald-300 group-hover:bg-emerald-50/30 transition-colors">
        <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center overflow-hidden flex-shrink-0">
          {photoUrl ? <img src={photoUrl} alt={name} className="w-full h-full object-cover" /> : <span className="text-base font-bold text-emerald-700">{initial}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-zinc-900 text-sm truncate">{name}</p>
            {ratingCount > 0 ? (
              <div className="flex items-center gap-1 text-xs text-zinc-600">
                <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                <span className="font-medium">{avgRating}/5</span>
                <span className="text-zinc-400">({ratingCount})</span>
              </div>
            ) : (
              <span className="text-xs text-zinc-400">Nouveau</span>
            )}
            {isSuperstar && (
              <Badge className="bg-gradient-to-r from-amber-400 to-amber-500 text-white border-0 gap-1 text-[10px] py-0 px-1.5 h-4"><Award className="h-2.5 w-2.5" /> Superstar</Badge>
            )}
          </div>
          {bio && <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{bio}</p>}
          <p className="text-[11px] text-emerald-700 mt-1 font-medium opacity-0 group-hover:opacity-100 transition-opacity">Voir le profil et les avis &rarr;</p>
        </div>
      </div>
    </Link>
  );
}

function BookingCard({ booking, index, viewer, onCancel, onRate, onApprove, onReject, onPay, onCheckoutReport, highlighted }) {
  // Once the stay is over, the badge always shows "Terminée" regardless of stored status.
  const si = booking.is_completed ? STATUS_MAP.completed : (STATUS_MAP[booking.status] || STATUS_MAP.pending);
  const pi = PAYMENT_MAP[booking.payment_status] || PAYMENT_MAP.pending;
  const isOwnerView = booking.owner_id === viewer?.id;
  const isRenterView = booking.renter_id === viewer?.id;
  const report = booking.checkout_report;
  const reportPhoto = report?.photo_url?.startsWith("/api/") ? `${BACKEND_URL}${report.photo_url}` : report?.photo_url;

  return (
    <Card
      id={`booking-${booking.id}`}
      className={`border rounded-2xl bg-white transition-all duration-700 ${highlighted ? "border-emerald-500 ring-2 ring-emerald-300 shadow-lg" : "border-zinc-200"}`}
      data-testid={`booking-card-${index}`}
    >
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-heading font-bold text-zinc-950 truncate">{booking.space_title}</h3>
            <p className="text-sm text-zinc-500 flex items-center gap-1 mt-1"><MapPin className="h-3 w-3" /> {booking.space_address || ""}</p>
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <Badge className={`${si.className} border text-xs`}>{si.label}</Badge>
            {booking.payment_status && booking.payment_status !== "not_required_yet" && <Badge className={`${pi.className} border-0 text-xs`}>{pi.label}</Badge>}
          </div>
        </div>

        {/* Profile of the OTHER party */}
        {booking.other_user_name && (
          <UserProfileMini
            name={booking.other_user_name}
            photo={booking.other_user_photo}
            avgRating={booking.other_user_avg_rating || 0}
            ratingCount={booking.other_user_rating_count || 0}
            isSuperstar={booking.other_user_is_superstar}
            bio={booking.other_user_bio}
            userId={booking.other_user_id}
          />
        )}

        {/* Request message from renter (only visible to owner when pending/approved) */}
        {booking.request_message && isOwnerView && (
          <div className="text-sm text-zinc-700 p-3 rounded-xl bg-emerald-50/50 border border-emerald-100">
            <p className="text-xs font-semibold text-emerald-700 mb-1">Message du campeur :</p>
            <p className="italic">"{booking.request_message}"</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 text-sm text-zinc-600">
          <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-zinc-400" /><span>{formatDate(booking.check_in)} - {formatDate(booking.check_out)}</span></div>
          <div className="flex items-center gap-2"><Moon className="h-4 w-4 text-zinc-400" /><span>{booking.total_nights} nuit{booking.total_nights > 1 ? "s" : ""}</span></div>
          {booking.num_spots > 1 && <div className="flex items-center gap-2"><Users className="h-4 w-4 text-zinc-400" /><span>{booking.num_spots} emplacement(s)</span></div>}
          {isOwnerView ? (
            <div className="flex items-center gap-2"><DollarSign className="h-4 w-4 text-zinc-400" /><span className="font-bold text-zinc-950">{(booking.host_payout ?? (booking.total_price > (booking.base_price || 0) ? booking.base_price : (booking.base_price || booking.total_price || 0) - (booking.platform_fee || 0)))?.toFixed(2)} $</span><span className="text-xs text-zinc-500 font-normal">(vous recevez)</span></div>
          ) : (
            <div className="flex items-center gap-2"><DollarSign className="h-4 w-4 text-zinc-400" /><span className="font-bold text-zinc-950">{booking.total_price?.toFixed(2)} $</span></div>
          )}
        </div>

        {booking.status === "pending_approval" && booking.expires_at && (
          <p className="text-xs text-amber-700 flex items-center gap-1"><Hourglass className="h-3 w-3" /> Réponse attendue avant {formatDate(booking.expires_at)}</p>
        )}
        {booking.status === "rejected" && booking.reject_reason && (
          <p className="text-xs text-red-600">Motif : {booking.reject_reason}</p>
        )}

        {/* Renter prompt: confirm checkout (after stay, photo + comment required) */}
        {isRenterView && booking.can_submit_checkout_report && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex flex-col sm:flex-row sm:items-start gap-3" data-testid={`checkout-prompt-${index}`}>
            <div className="flex items-start gap-2 flex-1 min-w-0">
              <Camera className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-900">Séjour terminé — confirmez votre départ</p>
                <p className="text-xs text-amber-700 mt-0.5">Laissez un mot au propriétaire (et idéalement une photo) pour clôturer ce séjour.</p>
              </div>
            </div>
            <Button size="sm" className="rounded-xl bg-amber-600 text-white hover:bg-amber-700 flex-shrink-0 w-full sm:w-auto" onClick={() => onCheckoutReport(booking)} data-testid={`checkout-report-btn-${index}`}>
              <Camera className="h-3.5 w-3.5 mr-1" /> Confirmer mon départ
            </Button>
          </div>
        )}

        {/* Checkout report - visible to both parties once submitted */}
        {report && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3 space-y-2" data-testid={`checkout-report-card-${index}`}>
            <div className="flex items-center gap-2 text-xs font-semibold text-emerald-800">
              <Camera className="h-3.5 w-3.5" /> Rapport de départ {isOwnerView && <span className="text-emerald-700 font-normal">- {report.renter_name}</span>}
            </div>
            {reportPhoto && (
              <img src={reportPhoto} alt="Etat du terrain au départ" className="w-full max-h-56 object-cover rounded-lg" data-testid={`checkout-report-photo-${index}`} />
            )}
            {report.comment && <p className="text-sm text-zinc-700 italic">"{report.comment}"</p>}
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          {/* Payment deadline countdown banner */}
          {isRenterView && booking.status === "approved" && booking.payment_status !== "paid" && booking.payment_deadline && (
            (() => {
              const remainingMs = new Date(booking.payment_deadline).getTime() - Date.now();
              if (remainingMs <= 0) return (
                <div className="w-full text-xs px-3 py-2 rounded-xl bg-red-50 text-red-700 border border-red-200" data-testid={`deadline-passed-${index}`}>
                  Délai de paiement expiré. Réservation annulée automatiquement.
                </div>
              );
              const hoursLeft = Math.floor(remainingMs / 3600000);
              const minsLeft = Math.floor((remainingMs % 3600000) / 60000);
              return (
                <div className="w-full text-xs px-3 py-2 rounded-xl bg-amber-50 text-amber-800 border border-amber-200" data-testid={`payment-deadline-${index}`}>
                  ⏱ Vous avez <strong>{hoursLeft}h{minsLeft.toString().padStart(2, "0")}min</strong> pour finaliser le paiement, sinon la réservation sera annulée automatiquement.
                </div>
              );
            })()
          )}
          {/* Owner actions for pending_approval */}
          {isOwnerView && booking.status === "pending_approval" && (
            <>
              <Button size="sm" className="rounded-xl bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => onApprove(booking)} data-testid={`approve-booking-${index}`}>
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Accepter
              </Button>
              <Button size="sm" variant="outline" className="rounded-xl text-red-600 border-red-200 hover:bg-red-50" onClick={() => onReject(booking)} data-testid={`reject-booking-${index}`}>
                <X className="h-3.5 w-3.5 mr-1" /> Refuser
              </Button>
            </>
          )}
          {/* Renter actions for approved or abandoned-payment bookings */}
          {isRenterView && (
            booking.status === "approved" ||
            (booking.status === "pending" && booking.payment_status !== "paid")
          ) && (
            <Button size="sm" className="rounded-xl bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => onPay(booking)} data-testid={`pay-booking-${index}`}>
              <CreditCard className="h-3.5 w-3.5 mr-1" /> {booking.status === "pending" ? "Reprendre le paiement" : "Confirmer et payer"}
            </Button>
          )}
          {/* Renter can cancel only while the stay has not started yet (and not already completed). */}
          {isRenterView
            && ["pending_approval", "pending", "confirmed"].includes(booking.status)
            && !booking.is_completed
            && new Date(booking.check_in) > new Date()
            && (
            <Button variant="outline" size="sm" className="rounded-xl text-red-500 border-red-200 hover:bg-red-50" onClick={() => onCancel(booking)} data-testid={`cancel-booking-${index}`}>Annuler</Button>
          )}
          {booking.can_rate && (
            <Button size="sm" className="rounded-xl bg-amber-500 text-white hover:bg-amber-600" onClick={() => onRate(booking)} data-testid={`rate-booking-${index}`}>
              <Star className="h-3.5 w-3.5 mr-1" /> Évaluer
            </Button>
          )}
          {booking.is_completed && booking.rating_submitted && (
            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 gap-1"><CheckCircle2 className="h-3 w-3" /> Évaluation envoyée</Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function MyBookingsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [myBookings, setMyBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratingBooking, setRatingBooking] = useState(null);
  const [checkoutBooking, setCheckoutBooking] = useState(null);
  // rejectBooking/rejectReason kept for legacy props compatibility; not used in renter view
  const [rejectBooking, setRejectBooking] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const initialTab = searchParams.get("tab") === "completed" ? "completed" : "upcoming";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [highlightId, setHighlightId] = useState(searchParams.get("booking") || null);
  const didScrollRef = useRef(false);

  const load = () => api.get("/bookings/my")
    .then(r => setMyBookings(r.data))
    .catch(() => {})
    .finally(() => setLoading(false));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // After bookings are loaded, scroll to the highlighted booking once and clear URL params
  useEffect(() => {
    if (didScrollRef.current || loading || !highlightId) return;
    // Make sure we're on the right tab for the target booking
    const target = myBookings.find(b => b.id === highlightId);
    if (target) {
      const wanted = target.is_completed ? "completed" : "upcoming";
      if (activeTab !== wanted) { setActiveTab(wanted); return; }
    }
    const el = document.getElementById(`booking-${highlightId}`);
    if (el) {
      didScrollRef.current = true;
      setTimeout(() => { el.scrollIntoView({ behavior: "smooth", block: "center" }); }, 150);
      const t = setTimeout(() => {
        setHighlightId(null);
        setSearchParams({}, { replace: true });
      }, 4500);
      return () => clearTimeout(t);
    }
  }, [loading, highlightId, activeTab, myBookings, setSearchParams]);

  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelPreview, setCancelPreview] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  const cancelBooking = async (b) => {
    setCancelTarget(b);
    setCancelPreview(null);
    setCancelLoading(false);
    try {
      const { data } = await api.get(`/bookings/${b.id}/cancellation-preview`);
      setCancelPreview(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
      setCancelTarget(null);
    }
  };
  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelLoading(true);
    try {
      const { data } = await api.put(`/bookings/${cancelTarget.id}/cancel`);
      if (data.refund_amount > 0) {
        toast.success(`Réservation annulée. Remboursement de ${data.refund_amount.toFixed(2)} $ initié sur votre carte (5-10 jours ouvrables).`);
      } else {
        toast.success("Réservation annulée");
      }
      setCancelTarget(null);
      setCancelPreview(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'annulation");
    } finally {
      setCancelLoading(false);
    }
  };

  const approveBooking = async (b) => {
    setActionLoading(true);
    try { await api.put(`/bookings/${b.id}/approve`); toast.success("Demande acceptée ! Le campeur a été notifié."); load(); }
    catch (err) {
      const detail = err.response?.data?.detail || "Erreur";
      if (err.response?.status === 403 && /Stripe/i.test(detail)) {
        toast.error(detail, {
          duration: 8000,
          action: { label: "Configurer Stripe", onClick: () => { window.location.href = "/profile"; } },
        });
      } else {
        toast.error(detail);
      }
    }
    finally { setActionLoading(false); }
  };

  const submitReject = async () => {
    if (!rejectBooking) return;
    setActionLoading(true);
    try {
      await api.put(`/bookings/${rejectBooking.id}/reject`, { reason: rejectReason });
      toast.success("Demande refusée");
      setRejectBooking(null); setRejectReason("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setActionLoading(false); }
  };

  const payBooking = async (b) => {
    setActionLoading(true);
    try {
      const { data } = await api.post(`/bookings/${b.id}/pay`, { origin_url: window.location.origin });
      if (data.free_mode) {
        toast.success(data.message || "Réservation confirmée!");
        load();
      } else if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setActionLoading(false); }
  };

  const openRate = (b) => setRatingBooking({ ...b, viewer_id: user?.id });

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-zinc-300 border-t-emerald-700 rounded-full" /></div>;

  const upcomingBookings = myBookings.filter(b => !b.is_completed && b.status !== "cancelled" && b.status !== "rejected" && b.status !== "expired");
  const completedBookings = myBookings.filter(b => b.is_completed);
  const otherBookings = myBookings.filter(b => !upcomingBookings.includes(b) && !completedBookings.includes(b));

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950 mb-2" data-testid="my-bookings-title">Mes réservations</h1>
      <p className="text-sm text-zinc-500 mb-6">Vos séjours en tant que campeur — à venir et passés.</p>
      <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="bookings-tabs">
        <TabsList className="mb-6 bg-zinc-100 rounded-xl p-1">
          <TabsTrigger value="upcoming" className="rounded-lg gap-2" data-testid="tab-upcoming">
            <CalendarDays className="h-3.5 w-3.5" /> À venir ({upcomingBookings.length})
          </TabsTrigger>
          <TabsTrigger value="completed" className="rounded-lg gap-2" data-testid="tab-completed">
            <CheckCircle2 className="h-3.5 w-3.5" /> Terminées ({completedBookings.length})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="upcoming">
          {upcomingBookings.length === 0 ? (
            <div className="text-center py-16" data-testid="no-bookings">
              <CalendarDays className="h-12 w-12 text-zinc-300 mx-auto mb-4" />
              <p className="text-zinc-500">Aucune réservation à venir</p>
              <Link to="/spaces"><Button className="mt-4 bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl">Explorer les terrains</Button></Link>
            </div>
          ) : (
            <div className="space-y-4">{upcomingBookings.map((b, i) => <BookingCard key={b.id} booking={b} index={i} viewer={user} onCancel={cancelBooking} onRate={openRate} onApprove={approveBooking} onReject={(b) => setRejectBooking(b)} onPay={payBooking} onCheckoutReport={setCheckoutBooking} highlighted={highlightId === b.id} />)}</div>
          )}
          {otherBookings.length > 0 && (
            <div className="mt-8">
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3">Annulées / Refusées / Expirées ({otherBookings.length})</p>
              <div className="space-y-4">{otherBookings.map((b, i) => <BookingCard key={b.id} booking={b} index={i} viewer={user} onCancel={cancelBooking} onRate={openRate} onApprove={approveBooking} onReject={(b) => setRejectBooking(b)} onPay={payBooking} onCheckoutReport={setCheckoutBooking} highlighted={highlightId === b.id} />)}</div>
            </div>
          )}
        </TabsContent>
        <TabsContent value="completed">
          {completedBookings.length === 0 ? (
            <div className="text-center py-16" data-testid="no-completed">
              <CheckCircle2 className="h-12 w-12 text-zinc-300 mx-auto mb-4" />
              <p className="text-zinc-500">Aucun séjour terminé pour l'instant</p>
              <p className="text-xs text-zinc-400 mt-1">Vos séjours passés apparaîtront ici.</p>
            </div>
          ) : (
            <div className="space-y-4">{completedBookings.map((b, i) => <BookingCard key={b.id} booking={b} index={i} viewer={user} onCancel={cancelBooking} onRate={openRate} onApprove={approveBooking} onReject={(b) => setRejectBooking(b)} onPay={payBooking} onCheckoutReport={setCheckoutBooking} highlighted={highlightId === b.id} />)}</div>
          )}
        </TabsContent>
      </Tabs>

      <RatingDialog booking={ratingBooking} open={!!ratingBooking} onOpenChange={(v) => !v && setRatingBooking(null)} onSubmitted={load} />
      <CheckoutReportDialog booking={checkoutBooking} open={!!checkoutBooking} onOpenChange={(v) => !v && setCheckoutBooking(null)} onSubmitted={load} />

      {/* Reject dialog */}
      <Dialog open={!!rejectBooking} onOpenChange={(v) => { if (!v) { setRejectBooking(null); setRejectReason(""); } }}>
        <DialogContent className="rounded-2xl" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Refuser la demande</DialogTitle>
            <DialogDescription>Indiquez une raison (optionnel). Le campeur sera notifié.</DialogDescription>
          </DialogHeader>
          <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Ex: Dates indisponibles, terrain en entretien..." className="rounded-xl min-h-[80px]" maxLength={300} data-testid="reject-reason-input" />
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setRejectBooking(null)} data-testid="reject-cancel-btn">Annuler</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700 rounded-xl" onClick={submitReject} disabled={actionLoading} data-testid="reject-confirm-btn">{actionLoading ? "Envoi..." : "Refuser"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel booking dialog with refund preview */}
      <Dialog open={!!cancelTarget} onOpenChange={(v) => { if (!v) { setCancelTarget(null); setCancelPreview(null); } }}>
        <DialogContent className="rounded-2xl" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600">Annuler ma réservation</DialogTitle>
            <DialogDescription>
              Politique : gratuit à plus de 7 jours · 30 % de frais entre 7 j et 48 h · annulation impossible à moins de 48 h.
            </DialogDescription>
          </DialogHeader>
          {!cancelPreview ? (
            <div className="text-center py-8 text-sm text-zinc-500">
              <Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" />
              Calcul du remboursement...
            </div>
          ) : (
            <div className="space-y-3">
              {cancelTarget && (
                <div className="p-3 rounded-xl bg-zinc-100 text-sm">
                  <p className="font-semibold text-zinc-900">{cancelTarget.space_title}</p>
                  <p className="text-xs text-zinc-600">
                    {cancelTarget.check_in?.slice(0, 10)} → {cancelTarget.check_out?.slice(0, 10)}
                  </p>
                </div>
              )}
              {!cancelPreview.can_cancel ? (
                <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-900" data-testid="cancel-impossible">
                  <p className="font-semibold">❌ Annulation impossible</p>
                  <p className="text-xs mt-1">Le séjour commence dans moins de 48 h ({cancelPreview.hours_until?.toFixed(0)} h restantes). Aucun remboursement n'est possible à ce stade.</p>
                </div>
              ) : cancelPreview.payment_status === "paid" ? (
                <div className={`p-3 rounded-xl border text-sm ${cancelPreview.tier === "free" ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="cancel-refund-preview">
                  <p className="font-semibold mb-2">{cancelPreview.tier === "free" ? "✅ Remboursement intégral" : `⚠️ Remboursement partiel (30 % de frais)`}</p>
                  <div className="space-y-1 text-xs text-zinc-700">
                    <div className="flex justify-between"><span>Montant payé</span><span className="font-medium">{cancelPreview.total_paid.toFixed(2)} $</span></div>
                    {cancelPreview.fee_amount > 0 && <div className="flex justify-between text-red-700"><span>Frais d'annulation ({cancelPreview.fee_pct} %)</span><span>− {cancelPreview.fee_amount.toFixed(2)} $</span></div>}
                    <div className="flex justify-between font-bold text-zinc-950 pt-1.5 border-t border-zinc-200"><span>Remboursement</span><span>{cancelPreview.refund_amount.toFixed(2)} $</span></div>
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-2">Le remboursement sera initié sur votre carte. Délai habituel : 5 à 10 jours ouvrables.</p>
                </div>
              ) : (
                <div className="p-3 rounded-xl bg-zinc-50 border border-zinc-200 text-sm text-zinc-700">
                  Cette réservation n'a pas encore été payée. L'annulation est gratuite et immédiate.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setCancelTarget(null)} disabled={cancelLoading}>Garder la réservation</Button>
            <Button
              className="bg-red-600 text-white hover:bg-red-700 rounded-xl"
              onClick={confirmCancel}
              disabled={cancelLoading || !cancelPreview || !cancelPreview.can_cancel}
              data-testid="confirm-cancel-btn"
            >
              {cancelLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Confirmer l'annulation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
